/**
 * FRONTEND COMMONS — browser-side reusables only.
 *
 * May import @shared/commons. May NOT be imported by the backend: this file is free
 * to touch the DOM and browser APIs.
 */

/* ---- fetch --------------------------------------------------------------- */

export interface ApiFailure {
  status: number;
  error: string;
  body: Record<string, unknown>;
}

export const isApiFailure = (e: unknown): e is ApiFailure =>
  typeof e === 'object' && e !== null && 'status' in e && 'error' in e;

/**
 * One fetch wrapper for every call: always sends credentials, always parses JSON,
 * always throws a typed ApiFailure on a non-2xx. Callers never repeat that.
 */
export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(path, {
    credentials: 'include',
    ...init,
    headers: {
      ...(init.body ? { 'content-type': 'application/json' } : {}),
      ...init.headers,
    },
  });

  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw { status: res.status, error: String(body.error ?? 'REQUEST_FAILED'), body } satisfies ApiFailure;
  }
  return body as T;
}

export const post = <T>(path: string, data?: unknown): Promise<T> =>
  api<T>(path, { method: 'POST', body: data === undefined ? undefined : JSON.stringify(data) });

export const put = <T>(path: string, data: unknown): Promise<T> =>
  api<T>(path, { method: 'PUT', body: JSON.stringify(data) });

export const get = <T>(path: string): Promise<T> => api<T>(path);

/* ---- DOM ----------------------------------------------------------------- */

/** Conditional class names, without pulling in clsx. */
export const cx = (...parts: (string | false | null | undefined)[]): string =>
  parts.filter(Boolean).join(' ');

export const setCssVar = (name: string, value: string): void =>
  document.documentElement.style.setProperty(name, value);

export function onKey(key: string, fn: () => void): () => void {
  const handler = (e: KeyboardEvent) => { if (e.key === key) fn(); };
  window.addEventListener('keydown', handler);
  return () => window.removeEventListener('keydown', handler);
}
